import { effect } from '@angular/core';

/**
 * Minimal surface required by {@link syncPhoenixHostInjector}. Implement with an `@Injectable` in the
 * **plugin app** (not in this package) so Angular AOT can compile it — injectables bundled from this
 * library are built with tsup and lack Ivy metadata, which triggers JIT failures in custom elements.
 *
 * Generic so the injector type comes from the plugin's `@angular/core`, not this package's.
 */
export interface PhoenixHostInjectorSink<T = unknown> {
  setHostInjector(injector: T): void;
}

/**
 * Keeps `hostInjector` in sync with a sink (typically your app's `PhoenixHostBridgeService`).
 *
 * @param createEffect Optional. Defaults to this package's `effect`. Pass the plugin app's own
 * `effect` when portaling shared-ui across a separate `@angular/core` copy (avoids NG0203).
 *
 * @example
 * ```ts
 * // Normal (published package / single Angular copy):
 * private readonly _sync = syncPhoenixHostInjector(this.hostBridge, this.hostInjector);
 *
 * // Portal / dual Angular:
 * private readonly _sync = syncPhoenixHostInjector(this.hostBridge, this.hostInjector, effect);
 * ```
 */
export function syncPhoenixHostInjector<T>(
  hostBridge: PhoenixHostInjectorSink<T>,
  hostInjector: () => T | undefined | null,
  createEffect: (fn: () => void) => unknown = effect,
): unknown {
  return createEffect(() => {
    const inj = hostInjector();
    if (inj != null) {
      hostBridge.setHostInjector(inj);
    }
  });
}
